import { useEffect } from 'react';
import { api } from '../utils/api';
import { useEditorStore, DEFAULT_DC_SETTINGS } from '../store/editorStore';
import { mapApiRack } from '../utils/draftUtils';
import { PPM } from '../store/editorStore';
import { aisleZoneToEditor, trayNodeToEditor } from '../utils/floorPlan.logic';
import { editorLoadPaths, preserveUnsavedEditorRacks } from './useDraftLoader.logic';
import { normalizeRackStatus, normalizeRackRole } from '../utils/rackLifecycle.logic';

export function useDraftLoader(code: string | undefined, currentDraftId: number | null, dc: any) {
  useEffect(() => {
    if (!code || !currentDraftId) return;
    const paths = editorLoadPaths(code, currentDraftId)
    if (!paths.draft) return

    Promise.all([
      api.get(paths.draft),
      api.get(paths.racks),
    ]).then(([draftRes, racksRes]) => {
      const draft = draftRes.data;
      const masterRacks = racksRes.data || [];
      const diff = draft.racks_json && typeof draft.racks_json === 'object' && !Array.isArray(draft.racks_json)
        ? draft.racks_json
        : null;
      const added = Array.isArray(draft.racks_json)
        ? draft.racks_json
        : (diff?.added || []);
      if (preserveUnsavedEditorRacks(useEditorStore.getState().racks, added, masterRacks.length)) {
        console.warn('[Editor] skipped stale draft load — unsaved racks present');
        return;
      }

      const racksRecord: Record<string, any> = {};
      
      masterRacks.forEach((rack: any) => {
        const mapped = mapApiRack(rack);
        racksRecord[mapped.id] = mapped;
      });
      
      useEditorStore.getState().setBaselineRacks(JSON.parse(JSON.stringify(racksRecord)));

      if (Array.isArray(draft.racks_json)) {
        const newRacksRecord: Record<string, any> = {};
        draft.racks_json.forEach((rack: any) => {
          const mapped = mapApiRack(rack, true);
          newRacksRecord[mapped.id] = mapped;
        });
        useEditorStore.getState().loadRacks(newRacksRecord);
      } else if (diff) {
        const deletedIds = diff.deleted || [];
        deletedIds.forEach((dbId: number) => {
          const key = Object.keys(racksRecord).find(k => racksRecord[k].dbId === dbId);
          if (key) delete racksRecord[key];
        });
        
        const updated = diff.updated || {};
        Object.entries(updated).forEach(([dbIdStr, rack]: [string, any]) => {
          const dbId = parseInt(dbIdStr);
          const key = Object.keys(racksRecord).find(k => racksRecord[k].dbId === dbId);
          if (key) {
             const existing = racksRecord[key];
             if (rack.x_m != null) existing.x = Math.round(rack.x_m * PPM);
             if (rack.y_m != null) existing.y = Math.round(rack.y_m * PPM);
             if (rack.rotation != null) existing.rotation = rack.rotation;
             if (rack.type) existing.rackType = rack.type;
             if (rack.u_slots != null) existing.uCount = rack.u_slots;
             if (rack.is_facility != null) existing.isFacility = Boolean(rack.is_facility);
             if (rack.is_blocked != null) existing.isBlocked = Boolean(rack.is_blocked);
             if (rack.tenant) existing.tenant = rack.tenant;
             if (rack.status) existing.status = normalizeRackStatus(rack.status);
             if (rack.role) existing.role = normalizeRackRole(rack.role, existing.rackType);
             if (rack.row_id !== undefined) existing.rowId = rack.row_id;
             if (rack.rack_pos_in_row !== undefined) existing.rackPosInRow = rack.rack_pos_in_row;
             // Проблема 5: эти поля раньше не применялись → физические габариты сбрасывались
             if (rack.width_m != null) { existing.widthM = rack.width_m; existing.width = Math.round(rack.width_m * PPM); }
             if (rack.depth_m != null) { existing.depthM = rack.depth_m; existing.height = Math.round(rack.depth_m * PPM); }
             if (rack.height_m != null) existing.heightM = rack.height_m;
          }
        });
        
        (diff.added || []).forEach((rack: any) => {
          const mapped = mapApiRack(rack, true);
          racksRecord[mapped.id] = mapped;
        });
      }

      const cableTraysData = draft.cable_trays_json || {};
      if (cableTraysData.racks_meta) {
        Object.entries(cableTraysData.racks_meta).forEach(([id, meta]: [string, any]) => {
          if (racksRecord[id]) {
            if (meta.status) racksRecord[id].status = normalizeRackStatus(meta.status);
            if (meta.role) racksRecord[id].role = normalizeRackRole(meta.role, racksRecord[id].rackType);
          }
        });
      }

      useEditorStore.getState().loadRacks(racksRecord);
      if (cableTraysData.rows_meta && Object.keys(cableTraysData.rows_meta).length > 0) {
        useEditorStore.getState().loadRows(cableTraysData.rows_meta);
      } else {
        api.get(`/api/datacenters/${code}/rows`).then((rowsRes) => {
          const rowsList: any[] = rowsRes.data || [];
          const rowsRecord: Record<string, any> = {};
          rowsList.forEach((r: any) => {
            rowsRecord[r.id] = {
              id: r.id,
              name: r.name,
              labelIndex: r.label_index,
              orientation: r.orientation ?? 0,
              color: r.color || '#3b82f6',
            };
          });
          useEditorStore.getState().loadRows(rowsRecord);
        }).catch(() => {
          useEditorStore.getState().loadRows({});
        });
      }
      if (cableTraysData.dc_settings) {
        useEditorStore.getState().loadDcSettings({ ...DEFAULT_DC_SETTINGS, ...cableTraysData.dc_settings });
      }

      // Проблема 8: загружаем aisle_zones из черновика
      if (cableTraysData.aisle_zones && Object.keys(cableTraysData.aisle_zones).length > 0) {
        const zonesRecord: Record<string, any> = {};
        Object.entries(cableTraysData.aisle_zones).forEach(([id, z]: [string, any]) => {
          zonesRecord[id] = aisleZoneToEditor(z, PPM);
        });
        useEditorStore.getState().loadAisleZones(zonesRecord);
      }

      if (draft.cable_trays_json) {
        const { nodes = {}, edges = {} } = draft.cable_trays_json;
        const parseNode = (n: any) => trayNodeToEditor(n, PPM);
        const nodesRec = Array.isArray(nodes) ? Object.fromEntries(nodes.map((n: any) => [n.id, parseNode(n)])) : Object.fromEntries(Object.entries(nodes).map(([k, n]: any) => [k, parseNode(n)]));
        const edgesRec = Array.isArray(edges) ? Object.fromEntries(edges.map((e: any) => [e.id, e])) : edges;
        useEditorStore.getState().setTrayGraph(nodesRec, edgesRec);
      } else {
        useEditorStore.getState().setTrayGraph({}, {});
      }
    }).catch(err => {
      console.error('[Editor] Failed to load draft:', err);
      if (err instanceof Error) {
        console.error(err.stack);
      }
    });
  }, [code, currentDraftId]);

  useEffect(() => {
    if (!code || currentDraftId) return;
    const { racks: racksPath } = editorLoadPaths(code, null)
    
    api.get(racksPath).then((racksRes) => {
      const apiRacks: any[] = racksRes.data || [];
      api.get(`/api/datacenters/${code}/rows`).then((rowsRes) => {
        const rowsList: any[] = rowsRes.data || [];
        const rowsRecord: Record<string, any> = {};
        rowsList.forEach((r: any) => {
          rowsRecord[r.id] = {
            id: r.id,
            name: r.name,
            labelIndex: r.label_index ?? 1,
            orientation: r.orientation ?? 0,
            color: r.color || '#3b82f6',
          };
        });
        useEditorStore.getState().loadRows(rowsRecord);
      }).catch(() => {
        useEditorStore.getState().loadRows({});
      });

      const racksRecord: Record<string, any> = {};
      apiRacks.forEach((rack: any) => {
        const mapped = mapApiRack(rack);
        racksRecord[mapped.id] = mapped;
      });
      useEditorStore.getState().loadRacks(racksRecord);
      useEditorStore.getState().setBaselineRacks(JSON.parse(JSON.stringify(racksRecord)));

      if (dc) {
        useEditorStore.getState().loadDcSettings({
          ...DEFAULT_DC_SETTINGS,
          namingMask: dc.rack_naming_template || DEFAULT_DC_SETTINGS.namingMask,
          module: dc.prefix || DEFAULT_DC_SETTINGS.module,
          defaultRackWidthM: dc.rack_step_m || DEFAULT_DC_SETTINGS.defaultRackWidthM,
          defaultRackDepthM: dc.rack_depth_m || DEFAULT_DC_SETTINGS.defaultRackDepthM,
          defaultRackHeightM: dc.rack_height_m || DEFAULT_DC_SETTINGS.defaultRackHeightM,
          defaultAisleWidthM: dc.aisle_width_m || DEFAULT_DC_SETTINGS.defaultAisleWidthM,
          defaultTrayHeightM: dc.tray_above_rack_m || DEFAULT_DC_SETTINGS.defaultTrayHeightM,
        });
      }

      if (dc?.aisle_zones_json && Object.keys(dc.aisle_zones_json).length > 0) {
        const zonesRecord: Record<string, any> = {};
        Object.entries(dc.aisle_zones_json).forEach(([id, z]: [string, any]) => {
          zonesRecord[id] = aisleZoneToEditor(z, PPM);
        });
        useEditorStore.getState().loadAisleZones(zonesRecord);
      }

      if (dc?.cable_trays_json) {
        const { nodes = {}, edges = {} } = dc.cable_trays_json;
        const parseNode = (n: any) => trayNodeToEditor(n, PPM);
        const nodesRec = Array.isArray(nodes) 
          ? Object.fromEntries(nodes.map((n: any) => [n.id, parseNode(n)])) 
          : Object.fromEntries(Object.entries(nodes).map(([k, n]: any) => [k, parseNode(n)]));
        const edgesRec = Array.isArray(edges) ? Object.fromEntries(edges.map((e: any) => [e.id, e])) : edges;
        useEditorStore.getState().setTrayGraph(nodesRec, edgesRec);
      } else {
        useEditorStore.getState().setTrayGraph({}, {});
      }
    }).catch(err => {
      console.error('[Editor] Failed to load published data:', err);
      if (err instanceof Error) {
        console.error(err.stack);
      }
    });
  }, [code, currentDraftId, dc]);
}
